import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';

import { RHFInputText } from '../../../components/rhf/RHFInputText';
import { RHFInputSelect } from '../../../components/rhf/RHFInputSelect';
import { RHFInputDate } from '../../../components/rhf/RHFInputDate';
import { RHFInputNumber } from '../../../components/rhf/RHFInputNumber';
import { RHFInputCheckbox } from '../../../components/rhf/RHFInputCheckbox';
import { RHFSearchableSelect } from '../../../components/rhf/RHFSearchableSelect';
import { contractSchema, ContractFormData, CONTRACT_TYPES, SALARY_MODES, THIRTEENTH_SALARY_MODES } from '../../../schemas/contract.schema';
import { configureZodErrors } from '../../../schemas/zod-error-map';
import { ContractDA } from '../../../services/admin/contract.service';
import { Contract } from '../../../models/employee.model';
import { getCantonOptions, resolveCanton } from '../../../data/cantons';
import { authCompany } from '../../../scripts/authentication.script';
import { modalShow, modalHide } from '../../../utils/modal.util';

export const CONTRACT_FORM_MODAL_ID = 'modal-contract-form';

interface ContractFormProps {
  companyId: string;
  employeeId: string;
  currentContract?: Contract | null;
  contractToEdit?: Contract | null;
  onSuccess: () => void;
  onCancel: () => void;
  lang: any;
  nextVersion?: number;
  autoOpen?: boolean;
}

/** Debounce delay for autocomplete requests (ms). */
const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const AUTOCOMPLETE_FIELDS: string[] = ['address_components'];

type AddressSource = 'company' | 'other';

const ContractForm: React.FC<ContractFormProps> = ({
  companyId, employeeId, currentContract, contractToEdit, onSuccess, onCancel, lang, nextVersion, autoOpen = false,
}) => {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      modalShow(CONTRACT_FORM_MODAL_ID);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isEditing = !!contractToEdit;

  // Company address from auth cookie — loaded once
  const company = useMemo(() => authCompany(), []);
  const companyAddress = company?.address ?? null;

  // Resolve company canton through resolveCanton for safety
  const companyCantonCode = useMemo(() => {
    if (!companyAddress?.administrative_area) return '';
    const resolved = resolveCanton(companyAddress.administrative_area);
    return resolved?.code ?? '';
  }, [companyAddress]);

  // Determine initial address source
  const getInitialAddressSource = (): AddressSource => {
    if (!contractToEdit) return 'company';
    // If editing and address differs from company, show "other"
    return 'company';
  };
  const [addressSource, setAddressSource] = useState<AddressSource>(getInitialAddressSource);

  configureZodErrors(lang.labels);

  // Build default workplace address values
  const buildCompanyAddressDefaults = () => ({
    street: companyAddress?.street ?? '',
    number: companyAddress?.number ?? '',
    complement: companyAddress?.additional ?? '',
    zip: companyAddress?.zip_code ?? '',
    city: companyAddress?.city ?? '',
    country: companyAddress?.country ?? 'CH',
  });

  const buildEditAddressDefaults = () => {
    const addr = contractToEdit?.workplace?.address;
    if (!addr) return null;
    return {
      street: addr.street ?? '',
      number: addr.number ?? '',
      complement: addr.complement ?? '',
      zip: addr.zip ?? '',
      city: addr.city ?? '',
      country: addr.country ?? 'CH',
    };
  };

  const { control, handleSubmit, watch, setValue } = useForm<ContractFormData>({
    resolver: zodResolver(contractSchema),
    mode: 'onBlur',
    defaultValues: contractToEdit ? {
      valid_from: contractToEdit.valid_from?.slice(0, 10) || '',
      contract_type: (contractToEdit.contract_type as any) || undefined,
      salary_mode: (contractToEdit.salary_mode as any) || undefined,
      hire_date: contractToEdit.hire_date?.slice(0, 10) || '',
      end_date: contractToEdit.end_date?.slice(0, 10) || '',
      probation_months: contractToEdit.probation_months ?? '',
      notice_period_months: contractToEdit.notice_period_months ?? '',
      activity_rate: contractToEdit.activity_rate ?? '',
      weekly_hours: contractToEdit.weekly_hours ?? '',
      base_salary: {
        amount: contractToEdit.base_salary?.amount ?? '',
        currency: contractToEdit.base_salary?.currency || 'CHF',
      },
      thirteenth_salary: {
        enabled: contractToEdit.thirteenth_salary?.enabled || false,
        mode: (contractToEdit.thirteenth_salary?.mode as any) || null,
      },
      vacation: {
        days_per_year: contractToEdit.vacation?.days_per_year ?? '',
        rate_percent: contractToEdit.vacation?.rate_percent ?? '',
      },
      workplace: {
        address: buildEditAddressDefaults(),
        canton: contractToEdit.workplace?.canton || '',
      },
      cct: {
        applicable: contractToEdit.cct?.applicable || false,
        name: contractToEdit.cct?.name || '',
      },
    } : {
      valid_from: '',
      contract_type: undefined,
      salary_mode: undefined,
      hire_date: '',
      end_date: '',
      probation_months: '',
      notice_period_months: '',
      activity_rate: '',
      weekly_hours: '',
      base_salary: { amount: '', currency: 'CHF' },
      thirteenth_salary: { enabled: false, mode: null },
      vacation: { days_per_year: '', rate_percent: '' },
      workplace: {
        address: companyAddress ? buildCompanyAddressDefaults() : null,
        canton: companyCantonCode,
      },
      cct: { applicable: false, name: '' },
    },
  });

  const contractType = watch('contract_type');
  const salaryMode = watch('salary_mode');
  const thirteenthEnabled = watch('thirteenth_salary.enabled');

  const langCode = lang.code?.toLowerCase() || 'fr';
  const cantonOptions = useMemo(() => getCantonOptions(langCode), [langCode]);
  const contractTypeOptions = CONTRACT_TYPES.map(t => ({ value: t, label: lang.labels[`contractType_${t}`] || t.replace(/_/g, ' ') }));
  const salaryModeOptions = SALARY_MODES.map(m => ({ value: m, label: lang.labels[`salaryMode_${m}`] || m }));
  const thirteenthModeOptions = THIRTEENTH_SALARY_MODES.map(m => ({ value: m, label: lang.labels[`thirteenthMode_${m}`] || m.replace(/_/g, ' ') }));

  // --- Google Maps autocomplete for "other address" ---
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [streetInput, setStreetInput] = useState('');

  useEffect(() => {
    if (window.google && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleStreetChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setStreetInput(input);
    setValue('workplace.address.street' as any, input);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!autocompleteServiceRef.current || !input) { setPredictions([]); return; }

    debounceRef.current = setTimeout(() => {
      if (!sessionTokenRef.current && window.google) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      autocompleteServiceRef.current!.getPlacePredictions(
        { input, componentRestrictions: { country: 'ch' }, sessionToken: sessionTokenRef.current! },
        (res) => setPredictions(res || []),
      );
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  }, [setValue]);

  const handleSelectPrediction = useCallback((item: google.maps.places.AutocompletePrediction) => {
    setPredictions([]);
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      { placeId: item.place_id, fields: AUTOCOMPLETE_FIELDS, sessionToken: sessionTokenRef.current! },
      (place, status) => {
        // Token consumed — reset for next session
        sessionTokenRef.current = null;

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        const components = place.address_components || [];
        const get = (type: string) => components.find(c => c.types.includes(type))?.long_name || '';
        const getShort = (type: string) => components.find(c => c.types.includes(type))?.short_name || '';

        const street = get('route');
        const number = get('street_number');
        const complement = '';
        const zip = get('postal_code');
        const city = get('locality');
        const country = getShort('country') || 'CH';
        const adminArea = get('administrative_area_level_1');

        setStreetInput(street);
        setValue('workplace.address.street' as any, street);
        setValue('workplace.address.number' as any, number);
        setValue('workplace.address.complement' as any, complement);
        setValue('workplace.address.zip' as any, zip);
        setValue('workplace.address.city' as any, city);
        setValue('workplace.address.country' as any, country);

        // Resolve canton from Google's administrative_area
        const resolved = resolveCanton(adminArea);
        if (resolved) {
          setValue('workplace.canton', resolved.code);
        }
      },
    );
  }, [setValue]);

  // Handle switching address source
  const handleAddressSourceChange = useCallback((source: AddressSource) => {
    setAddressSource(source);
    if (source === 'company') {
      setValue('workplace.address' as any, {
        street: companyAddress?.street ?? '',
        number: companyAddress?.number ?? '',
        complement: companyAddress?.additional ?? '',
        zip: companyAddress?.zip_code ?? '',
        city: companyAddress?.city ?? '',
        country: companyAddress?.country ?? 'CH',
      });
      if (companyCantonCode) setValue('workplace.canton', companyCantonCode);
      setStreetInput('');
    } else {
      setValue('workplace.address' as any, { street: '', number: '', complement: '', zip: '', city: '', country: 'CH' });
      setValue('workplace.canton', '');
      setStreetInput('');
    }
    setPredictions([]);
  }, [setValue, companyCantonCode, companyAddress]);

  const closeModalThen = (callback: () => void) => {
    const element = document.getElementById(CONTRACT_FORM_MODAL_ID);
    if (element) {
      element.addEventListener('hidden.bs.modal', callback, { once: true });
    }
    modalHide(CONTRACT_FORM_MODAL_ID);
  };

  const onSubmit = async (data: any) => {
    setSubmitting(true);

    let response;
    if (isEditing && contractToEdit) {
      response = await ContractDA.putContract(companyId, employeeId, contractToEdit.id, data);
    } else {
      response = await ContractDA.postContract(companyId, employeeId, data);
    }

    if (response.status === 201 || response.status === 200) {
      Swal.fire({
        title: isEditing
          ? (lang.labels.successfullyUpdated || 'Updated successfully')
          : (lang.labels.successfullyCreated || 'Created successfully'),
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
      closeModalThen(() => onSuccess());
    } else {
      Swal.fire({
        title: lang.labels.error || 'Error',
        text: response.data?.message || response.message || 'An error occurred',
        icon: 'error',
      });
    }

    setSubmitting(false);
  };

  const handleCancel = () => {
    closeModalThen(() => onCancel());
  };

  // Modal title: distinguish create new version vs correct existing version
  const modalTitle = isEditing
    ? `${lang.labels.correctContract || 'Correct contract'} (v${contractToEdit?.version})`
    : `${lang.labels.newContractVersion || 'New contract version'}${nextVersion ? ` (v${nextVersion})` : ''}`;

  return (
    <div id={CONTRACT_FORM_MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{modalTitle}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={handleCancel}>
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body">
            {!isEditing && currentContract && (
              <div className="alert alert-warning mb-5">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {(lang.labels.newVersionWarning || 'This will create version {version} of the contract, effective from the date you set. Version {prevVersion} will be closed automatically.')
                  .replace('{version}', String(nextVersion || ''))
                  .replace('{prevVersion}', String(currentContract.version || ''))
                }
              </div>
            )}

            {isEditing && (
              <div className="alert alert-info mb-5">
                <i className="bi bi-pencil me-2"></i>
                {(lang.labels.correctVersionInfo || 'You are correcting version {version}. This will update the existing version without creating a new one.')
                  .replace('{version}', String(contractToEdit?.version || ''))
                }
              </div>
            )}

            <form id="contract-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                <div className="col-md-4">
                  <RHFInputDate name="valid_from" control={control} label={lang.labels.validFrom || 'Effective from'} required disabled={isEditing} />
                </div>
                <div className="col-md-4">
                  <RHFInputSelect name="contract_type" control={control} label={lang.labels.contractType || 'Contract type'} options={contractTypeOptions} required placeholder={lang.labels.select || 'Select...'} />
                </div>
                <div className="col-md-4">
                  <RHFInputSelect name="salary_mode" control={control} label={lang.labels.salaryMode || 'Salary mode'} options={salaryModeOptions} required placeholder={lang.labels.select || 'Select...'} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <RHFInputDate name="hire_date" control={control} label={lang.labels.hireDate || 'Hire date'} required />
                </div>
                {contractType === 'fixed_term' && (
                  <div className="col-md-4">
                    <RHFInputDate name="end_date" control={control} label={lang.labels.endDate} required />
                  </div>
                )}
              </div>

              <div className="separator my-5"></div>

              <div className="row">
                <div className="col-md-3">
                  <RHFInputNumber name="activity_rate" control={control} label={lang.labels.activityRate} required min={0} max={100} suffix="%" />
                </div>
                <div className="col-md-3">
                  <RHFInputNumber name="weekly_hours" control={control} label={lang.labels.weeklyHours || 'Weekly hours'} step="0.5" />
                </div>
                <div className="col-md-3">
                  <RHFInputNumber name="probation_months" control={control} label={lang.labels.probationMonths || 'Probation (months)'} />
                </div>
                <div className="col-md-3">
                  <RHFInputNumber name="notice_period_months" control={control} label={lang.labels.noticePeriod || 'Notice period (months)'} />
                </div>
              </div>

              <div className="separator my-5"></div>
              <h5 className="fw-bold mb-3">{lang.labels.salary || 'Salary'}</h5>

              <div className="row">
                <div className="col-md-4">
                  <RHFInputNumber name="base_salary.amount" control={control} label={lang.labels.baseSalary || 'Base salary'} required step="0.01" />
                </div>
                <div className="col-md-2">
                  <RHFInputText name="base_salary.currency" control={control} label={lang.labels.currency || 'Currency'} disabled />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <RHFInputCheckbox name="thirteenth_salary.enabled" control={control} label={lang.labels.thirteenthSalary || '13th salary'} />
                </div>
                {thirteenthEnabled && (
                  <div className="col-md-4">
                    <RHFInputSelect name="thirteenth_salary.mode" control={control} label={lang.labels.thirteenthMode || 'Payment mode'} options={thirteenthModeOptions} required placeholder={lang.labels.select || 'Select...'} />
                    <div className="form-text text-muted mt-1" style={{ whiteSpace: 'pre-line' }}>{lang.labels.thirteenthModeHelp}</div>
                  </div>
                )}
              </div>

              <div className="separator my-5"></div>
              <h5 className="fw-bold mb-3">{lang.labels.vacation || 'Vacation'}</h5>

              <div className="row">
                {salaryMode === 'monthly' && (
                  <div className="col-md-4">
                    <RHFInputNumber name="vacation.days_per_year" control={control} label={lang.labels.vacationDays || 'Days per year'} required step="0.5" />
                  </div>
                )}
                {salaryMode === 'hourly' && (
                  <div className="col-md-4">
                    <RHFInputNumber name="vacation.rate_percent" control={control} label={lang.labels.vacationRate || 'Vacation rate (%)'} required step="0.001" suffix="%" />
                  </div>
                )}
              </div>

              <div className="separator my-5"></div>
              <h5 className="fw-bold mb-3">{lang.labels.workplace || 'Workplace'}</h5>

              <div className="d-flex gap-4 mb-4">
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="addressSource" id="addressSourceCompany" checked={addressSource === 'company'} onChange={() => handleAddressSourceChange('company')} />
                  <label className="form-check-label" htmlFor="addressSourceCompany">{lang.labels.workplaceAddressCompany || 'Company address'}</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="addressSource" id="addressSourceOther" checked={addressSource === 'other'} onChange={() => handleAddressSourceChange('other')} />
                  <label className="form-check-label" htmlFor="addressSourceOther">{lang.labels.workplaceAddressOther || 'Other address'}</label>
                </div>
              </div>

              {addressSource === 'company' ? (
                <div className="row mb-3">
                  <div className="col-md-8">
                    <div className="bg-light-primary rounded p-4">
                      {companyAddress ? (
                        <div className="text-gray-700">
                          {[
                            [companyAddress.street, companyAddress.number].filter(Boolean).join(' '),
                            companyAddress.additional || null,
                            [companyAddress.zip_code, companyAddress.city].filter(Boolean).join(' '),
                            companyAddress.country,
                          ].filter(Boolean).map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">{lang.labels.noAddress || 'No address available'}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <RHFSearchableSelect name="workplace.canton" control={control} label={lang.labels.workCanton} options={cantonOptions} required placeholder={lang.labels.select || 'Select...'} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="row">
                    <div className="col-md-6 position-relative">
                      <div className="fv-row mb-5">
                        <label className="form-label">{lang.labels.street || 'Street'}</label>
                        <input className="form-control" type="text" autoComplete="off" value={streetInput} onChange={handleStreetChange} placeholder={lang.labels.searchAddress || 'Search address...'} />
                        {predictions.length > 0 && (
                          <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1060, maxHeight: 250, overflowY: 'auto' }}>
                            {predictions.map((item, idx) => (
                              <li key={idx} className="list-group-item list-group-item-action cursor-pointer" style={{ cursor: 'pointer' }} onClick={() => handleSelectPrediction(item)}>
                                {item.description}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <RHFInputText name="workplace.address.number" control={control} label={lang.labels.streetNumber || 'Number'} />
                    </div>
                    <div className="col-md-3">
                      <RHFInputText name="workplace.address.complement" control={control} label={lang.labels.complement || 'Complement'} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3">
                      <RHFInputText name="workplace.address.zip" control={control} label={lang.labels.zipCode || 'Zip code'} />
                    </div>
                    <div className="col-md-5">
                      <RHFInputText name="workplace.address.city" control={control} label={lang.labels.city || 'City'} />
                    </div>
                    <div className="col-md-4">
                      <RHFSearchableSelect name="workplace.canton" control={control} label={lang.labels.workCanton} options={cantonOptions} required placeholder={lang.labels.select || 'Select...'} />
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light me-3" type="button" onClick={handleCancel}>
              {lang.labels.cancel || 'Cancel'}
            </button>
            <button className="btn btn-primary" type="submit" form="contract-form" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2"></span>}
              {isEditing ? (lang.labels.save || 'Save') : (lang.labels.create || 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractForm;
